import { useQuery } from '@tanstack/react-query';

import { getTriggerProfile } from '@/lib/api/generated/api';
import type { TriggerProfileView } from '@/lib/api/generated/model';

import { triggerKeys } from '../queryKeys';

/**
 * One trigger profile's full stored row — backs `TriggerProfileDetailSheet`'s
 * config summary.
 *
 * Same hand-written-`queryFn` convention as `useTriggerProfiles`
 * (research.md's confirmed-pattern note): calls the generated raw
 * `getTriggerProfile` fetcher directly, not the generated
 * `getGetTriggerProfileQueryOptions` helper — no feature in this codebase
 * actually consumes those.
 */
export function useTriggerProfile(
  workspaceId: string,
  triggerProfileId: string,
) {
  const query = useQuery({
    queryKey: triggerKeys.detail(workspaceId, triggerProfileId),
    queryFn: async (): Promise<TriggerProfileView> => {
      const response = await getTriggerProfile(workspaceId, triggerProfileId);
      if (response.status !== 200) {
        throw new Error('The trigger profile could not be loaded.');
      }
      return response.data;
    },
    retry: false,
  });

  return {
    profile: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
  };
}
